import React, { useState, useEffect } from 'react';
import { Clock, Settings, Wifi, WifiOff, Activity, HelpCircle, BookOpen, Sun, Moon } from 'lucide-react';
import appLogoPrimary from '../../assets/icons/Polaris-Tech-Bridge-logo-primary.png';
import appLogoWhite from '../../assets/icons/Polaris-Tech-Bridge-logo-white.png';
import poweredLogoWhite from '../../assets/icons/Powered--Blue-White-Narrow.png';
import poweredLogoPrimary from '../../assets/icons/powered_by_bluemvmt.png';
import EncryptedStorage from '../../services/encryptedStorageService';

const Header = ({
  dataSource = "simulated",
  timeZone = "UTC",
  onTimeZoneChange,
  onSettingsClick,
  connectionStatus = "connected",
  dataQuality = null,
  showDataStatus = true,
  // NEW: Tutorial props
  showTutorial = false,
  onTutorialToggle,
  tutorialStep = 0,
  isFirstTimeUser = false
}) => {

  const [currentTime, setCurrentTime] = useState(new Date());
  const [showSettings, setShowSettings] = useState(false);
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('bluemvnt_theme') || 'dark';
  });

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('bluemvnt_theme', theme);
  }, [theme]);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const onTutorialToggleRef = React.useRef(onTutorialToggle);
  onTutorialToggleRef.current = onTutorialToggle;

  const [showFirstVisitBanner, setShowFirstVisitBanner] = useState(() => {
    try {
      const completed = EncryptedStorage.getItem('ocean-monitor-tutorial-completed');
      const dismissed = EncryptedStorage.getItem('ocean-monitor-tutorial-invite-dismissed');
      return isFirstTimeUser && !completed && !dismissed;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      const completed = EncryptedStorage.getItem('ocean-monitor-tutorial-completed');
      const dismissed = EncryptedStorage.getItem('ocean-monitor-tutorial-invite-dismissed');
      setShowFirstVisitBanner(Boolean(isFirstTimeUser && !completed && !dismissed && !showTutorial));
    } catch {
      setShowFirstVisitBanner(false);
    }
  }, [isFirstTimeUser, showTutorial]);

  const dismissFirstVisitBanner = () => {
    try {
      EncryptedStorage.setItem('ocean-monitor-tutorial-invite-dismissed', true);
    } catch {
      /* ignore */
    }
    setShowFirstVisitBanner(false);
  };

  const startTourFromBanner = () => {
    setShowFirstVisitBanner(false);
    onTutorialToggleRef.current?.(true);
  };

  // Format time based on selected timezone
  const getFormattedTime = () => {
    const options = {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    };

    switch (timeZone) {
      case 'UTC':
        return currentTime.toLocaleString('en-US', { ...options, timeZone: 'UTC' });
      case 'Local':
        return currentTime.toLocaleString('en-US', options);
      case 'CST':
        return currentTime.toLocaleString('en-US', { ...options, timeZone: 'America/Chicago' });
      default:
        return currentTime.toLocaleString('en-US', options);
    }
  };

  const getDataSourceDisplay = () => {
    switch (dataSource) {
      case 'api':
        return { text: 'API Stream', color: 'text-blue-600 dark:text-blue-400', icon: Wifi };
      case 'simulated':
        return { text: 'Simulated', color: 'text-amber-600 dark:text-yellow-400', icon: Activity };
      case 'none':
        return { text: 'No Data', color: 'text-red-600 dark:text-red-400', icon: WifiOff };
      default:
        return { text: 'Unknown', color: 'text-slate-500 dark:text-slate-400', icon: Activity };
    }
  };

  const getConnectionStatusIndicator = () => {
    switch (connectionStatus) {
      case 'connected':
        return <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse dark:bg-green-400"></div>;
      case 'connecting':
        return <div className="w-2 h-2 bg-yellow-500 rounded-full animate-ping dark:bg-yellow-400"></div>;
      case 'disconnected':
        return <div className="w-2 h-2 bg-red-500 rounded-full dark:bg-red-400"></div>;
      default:
        return <div className="w-2 h-2 bg-slate-400 rounded-full dark:bg-gray-400"></div>;
    }
  };

  const dataSourceInfo = getDataSourceDisplay();
  const DataSourceIcon = dataSourceInfo.icon;

  return (
    <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-3 md:px-6 py-3 md:py-4 relative shadow-sm dark:bg-slate-800 dark:border-slate-700">
      {/* Top Row - Logo and Main Controls */}
      <div className="flex items-center justify-between gap-4 mb-3">

        {/* Left Side - Logo */}
        <div className="flex items-center gap-3">
          <div className="w-[180px] h-[40px]">
            <img
              src={theme === 'dark' ? appLogoWhite : appLogoPrimary}
              alt="Polaris Tech Bridge"
              className="w-full h-full object-contain"
            />
          </div>
        </div>

        {/* Right Side - Controls */}
        <div className="flex items-center gap-2 md:gap-3">

          {/* Time Zone Selector */}
          <div className="flex items-center gap-2 text-xs md:text-sm">
            <Clock className="w-3 h-3 md:w-4 md:h-4 text-slate-400" />
            <select
              value={timeZone}
              onChange={(e) => onTimeZoneChange && onTimeZoneChange(e.target.value)}
              className="bg-slate-100 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded px-2 py-1 text-xs md:text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-slate-700 dark:border-slate-600"
            >
              <option value="UTC">UTC</option>
              <option value="Local">Local Time</option>
              <option value="CST">CST</option>
            </select>
          </div>

          {/* Current Time Display */}
          <div className="text-xs md:text-sm text-slate-600 dark:text-slate-300 font-mono dark:text-slate-300">
            {getFormattedTime()}
          </div>

          {/* Data Quality Indicator */}
          {showDataStatus && dataQuality && (
            <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded dark:text-slate-400 dark:bg-slate-700/50">
              <Activity className="w-3 h-3" />
              <span>{dataQuality.stations}S/{dataQuality.measurements}M</span>
            </div>
          )}

          {/* Tutorial Button */}
          <button
            onClick={() => onTutorialToggle && onTutorialToggle(!showTutorial)}
            className={`p-1.5 md:p-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 relative ${showTutorial
              ? 'bg-blue-600 text-white'
              : 'bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 text-slate-600 dark:text-slate-300'
              }`}
            aria-label="Tutorial"
            title="Interactive Tutorial"
          >
            <HelpCircle className="w-4 h-4 md:w-5 md:h-5" />
            {/* Tutorial step indicator */}
            {showTutorial && (
              <div className="absolute -top-1 -right-1 min-w-[1rem] h-4 px-0.5 bg-blue-500 text-white text-[10px] rounded-full flex items-center justify-center tabular-nums">
                {tutorialStep + 1}
              </div>
            )}
            {/* First-time user indicator */}
            {isFirstTimeUser && !EncryptedStorage.getItem('ocean-monitor-tutorial-completed') && (
              <div className="absolute -top-1 -right-1 w-2 h-2 bg-amber-400 rounded-full animate-pulse dark:bg-yellow-400"></div>
            )}
          </button>

          {/* Auth Section */}


          {/* Settings Button */}
          <div className="relative">
            <button
              onClick={() => {
                setShowSettings(!showSettings);
                onSettingsClick && onSettingsClick();
              }}
              className="p-1.5 md:p-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 text-slate-600 dark:text-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600"
              aria-label="Settings"
            >
              <Settings className="w-4 h-4 md:w-5 md:h-5" />
            </button>

            {/* Settings Dropdown */}
            {showSettings && (
              <div className="absolute top-full right-0 mt-2 w-64 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl z-50 dark:bg-slate-800 dark:border-slate-600">
                <div className="p-3">
                  <div className="text-sm font-semibold text-slate-800 dark:text-white mb-3 dark:text-slate-200">System Settings</div>

                  {/* Data Source Info */}
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-slate-500 dark:text-slate-400 dark:text-slate-400">Data Source:</span>
                      <span className={dataSourceInfo.color}>{dataSourceInfo.text}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500 dark:text-slate-400 dark:text-slate-400">Connection:</span>
                      <span className={
                        connectionStatus === 'connected' ? 'text-green-600 dark:text-green-300' :
                          connectionStatus === 'connecting' ? 'text-amber-600 dark:text-yellow-400' :
                            'text-red-600 dark:text-red-400'
                      }>
                        {connectionStatus.charAt(0).toUpperCase() + connectionStatus.slice(1)}
                      </span>
                    </div>
                    {dataQuality && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-slate-500 dark:text-slate-400 dark:text-slate-400">Stations:</span>
                          <span className="text-slate-700 dark:text-slate-200 dark:text-slate-300">{dataQuality.stations} Stations</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500 dark:text-slate-400 dark:text-slate-400">Measurements:</span>
                          <span className="text-slate-700 dark:text-slate-200 dark:text-slate-300">{dataQuality.measurements} Measurements</span>
                        </div>
                        {dataQuality.lastUpdate && (
                          <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-slate-400 dark:text-slate-400">Last Update:</span>
                            <span className="text-slate-700 dark:text-slate-200 dark:text-slate-300">
                              {new Date(dataQuality.lastUpdate).toLocaleTimeString()}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Quick Actions */}
                  <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700 dark:border-slate-700">
                    <div className="space-y-2">
                      {/* Theme Toggle option */}
                      <button
                        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                        className="w-full text-left text-xs text-slate-600 dark:text-slate-300 hover:text-slate-900 p-2 hover:bg-slate-100 dark:hover:bg-slate-700/50 rounded transition-colors flex items-center justify-between dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-700"
                      >
                        <div className="flex items-center gap-2">
                          {theme === 'dark' ? <Sun className="w-3 h-3" /> : <Moon className="w-3 h-3" />}
                          Switch to {theme === 'dark' ? 'Light' : 'Dark'} Theme
                        </div>
                      </button>

                      {/* Tutorial option in settings */}
                      <button
                        onClick={() => {
                          onTutorialToggle && onTutorialToggle(true);
                          setShowSettings(false);
                        }}
                        className="w-full text-left text-xs text-slate-600 dark:text-slate-300 hover:text-slate-900 p-2 hover:bg-slate-100 dark:hover:bg-slate-700/50 rounded transition-colors flex items-center gap-2 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-700"
                      >
                        <BookOpen className="w-3 h-3" />
                        Start Tutorial
                      </button>
                      <button
                        onClick={() => window.location.reload()}
                        className="w-full text-left text-xs text-slate-600 dark:text-slate-300 hover:text-slate-900 p-2 hover:bg-slate-100 dark:hover:bg-slate-700/50 rounded transition-colors dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-700"
                      >
                        Refresh Data
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Row - Subtitle and Data Status */}
      <div className="flex items-center justify-start gap-2 text-xs text-slate-500 dark:text-slate-400 dark:text-slate-400">
        <img
          src={theme === 'dark' ? poweredLogoWhite : poweredLogoPrimary}
          alt="Powered by Bluemvmt Digital Ocean"
          className="h-6 object-contain"
        />
        <div className="w-1 h-1 bg-slate-400 rounded-full dark:bg-slate-500"></div>
        <div className="flex items-center gap-1">
          <DataSourceIcon className="w-3 h-3" />
          <span className={dataSourceInfo.color}>Data: {dataSourceInfo.text}</span>
          {getConnectionStatusIndicator()}
        </div>
      </div>

      {/* First visit: optional tour (non-blocking; avoids auto-opening the modal on load) */}
      {showFirstVisitBanner && !showTutorial && (
        <div
          className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-blue-200 dark:border-blue-500/40 bg-blue-50/90 dark:bg-slate-800/95 px-3 py-2.5 text-sm text-slate-800 dark:text-slate-100 shadow-sm"
          role="region"
          aria-label="Platform introduction"
        >
          <p className="leading-snug pr-0 sm:pr-4">
            <span className="font-medium text-blue-900 dark:text-blue-200">New here?</span>{' '}
            Take a short guided tour of the map, controls, and assistant—or explore on your own.
          </p>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={dismissFirstVisitBanner}
              className="px-3 py-1.5 rounded-md text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-white/80 dark:hover:bg-slate-700/80 border border-transparent hover:border-slate-200 dark:hover:border-slate-600 transition-colors"
            >
              Not now
            </button>
            <button
              type="button"
              onClick={startTourFromBanner}
              className="px-3 py-1.5 rounded-md text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 shadow-sm transition-colors"
            >
              Take the tour
            </button>
          </div>
        </div>
      )}

      {/* Click outside to close dropdowns */}
      {showSettings && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowSettings(false);
          }}
        ></div>
      )}
    </header>
  );
};


export default Header;